import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import type { EChartsOption } from 'echarts';
import { debounce } from 'lodash';

interface DataItem {
  title?: string;              // 图表标题
  tooltip_title?: string;         // 悬浮窗标题
  月份: string;                // 月份，格式如 "2024-04"
  spec_name?: string;            // 规格名称
  spu_no?: string;             // SPU编码
  spu_name?: string;            // SPU名称
  销售金额: number;            // 销售金额
}

interface TwoYearSpuSpecSalesProps {
  data: DataItem[];
  loading?: boolean;
}

// 定义渐变色 - 100种有区分度的现代商务风颜色
const colors = [
  // 基础色彩系列 (10种)
  ['#1890ff', '#0050b3'],  // 蓝色系
  ['#52c41a', '#237804'],  // 绿色系
  ['#ffd666', '#ffc53d'],  // 浅金色系
  ['#f5222d', '#cf1322'],  // 红色系
  ['#722ed1', '#391085'],  // 紫色系
  ['#fa8c16', '#d46b08'],  // 橙色系
  ['#eb2f96', '#9e1068'],  // 粉色系
  ['#13c2c2', '#08979c'],  // 青色系
  ['#595959', '#262626'],  // 灰色系
  ['#a0d911', '#7cb305'],  // 黄绿系

  // 扩展色彩系列 - 明亮色调 (20种)
  ['#ff4d4f', '#cf1322'],  // 亮红色
  ['#ff7a45', '#d46b08'],  // 亮橙色
  ['#ffa940', '#fa8c16'],  // 亮黄色
  ['#fffb8f', '#faad14'],  // 亮浅黄
  ['#52c41a', '#237804'],  // 亮绿色
  ['#36cfc9', '#08979c'],  // 亮青色
  ['#1890ff', '#0958d9'],  // 亮蓝色
  ['#597ef7', '#2f54eb'],  // 亮靛蓝
  ['#9254de', '#722ed1'],  // 亮紫色
  ['#f759ab', '#eb2f96'],  // 亮粉色
  ['#ff13c2', '#a0d911'],  // 亮薄荷绿
  ['#ff9c6e', '#faad14'],  // 亮柠檬黄
  ['#b7282d', '#389e0d'],  // 亮深绿
  ['#4096ff', '#0958d9'],  // 亮天蓝
  ['#69c0ff', '#40a9ff'],  // 浅天蓝
  ['#91d5ff', '#69c0ff'],  // 极浅天蓝
  ['#b37feb', '#9254de'],  // 薰荷紫
  ['#d3adf7', '#b37feb'],  // 浅薰衣草紫
  ['#ffadd2', '#f759ab'],  // 浅粉色
  ['#ffccc7', '#ff7875'],  // 浅珊瑚色
  ['#ff9c6e', '#ff7a45'],  // 柔和珊瑚橙

  // 扩展色彩系列 - 中等色调 (25种)
  ['#73d13d', '#389e0d'],  // 草绿色
  ['#95de64', '#52c41a'],  // 薄荷绿
  ['#b7eb8f', '#73d13d'],  // 薄荷浅绿
  ['#40a9ff', '#1890ff'],  // 天蓝色
  ['#69c0ff', '#40a9ff'],  // 浅天蓝
  ['#91d5ff', '#69c0ff'],  // 极浅天蓝
  ['#b37feb', '#9254de'],  // 薰荷紫
  ['#d3adf7', '#b37feb'],  // 浅薰衣草紫
  ['#ffadd2', '#f759ab'],  // 浅粉色
  ['#ffccc7', '#ff7875'],  // 浅珊瑚色
  ['#ffd591', '#fa8c16'],  // 柔和橙
  ['#ffccc7', '#ff4d4f'],  // 柔和红
  ['#ff9c6e', '#ff7a45'],  // 柔和珊瑚橙
  ['#f759ab', '#eb2f96'],  // 粉粉色
  ['#13c2c2', '#08979c'],  // 青色系
  ['#40a9ff', '#1890ff'],  // 天蓝色
  ['#69c0ff', '#40a9ff'],  // 浅天蓝
  ['#91d5ff', '#69c0ff'],  // 极浅天蓝
  ['#b37feb', '#9254de'],  // 薰荷紫
  ['#d3adf7', '#b37feb'],  // 浅薰衣草紫

  // 扩展色彩系列 - 柔和色调 (25种)
  ['#d3f261', '#a0d911'],  // 韠和黄绿
  ['#95de64', '#52c41a'],  // 柔和绿
  ['#5cdbd3', '#13c2c2'],  // 柔和青绿
  ['#40a9ff', '#1890ff'],  // 柔和蓝
  ['#adc6ff', '#597ef7'],  // 柔和靛蓝
  ['#d3adf7', '#9254de'],  // 柔和紫
  ['#ffadd2', '#f759ab'],  // 柔和粉
  ['#ffd591', '#fa8c16'],  // 柔和橙
  ['#ffccc7', '#ff4d4f'],  // 柔和红
  ['#ff9c6e', '#ff7a45'],  // 柔和珊瑚橙
  ['#f759ab', '#eb2f96'],  // 粉粉色
  ['#a0d911', '#7cb305'],  // 黄绿系
  ['#95de64', '#52c41a'],  // 草绿色
  ['#5cdbd3', '#13c2c2'],  // 柔和青绿
  ['#40a9ff', '#1890ff'],  // 天蓝色
  ['#adc6ff', '#597ef7'],  // 柔和靛蓝
  ['#d3adf7', '#9254de'],  // 柔和紫
  ['#ffadd2', '#f759ab'],  // 柔和粉
  ['#ffd591', '#fa8c16'],  // 柔和橙
  ['#ffccc7', '#ff4d4f'],  // 柔和红
  ['#ff9c6e', '#ff7a45'],  // 柔和珊瑚橙
  ['#8c8cff', '#5cadff'],  // 浅蓝色
  ['#b4d3ff', '#722ed1'],  // 粉紫色
  ['#d9d7ff', '#52c41a'],  // 草绿色
  ['#ffd591', '#fa8c16'],  // 柔和橙
  ['#ff7875', '#ff4d4f'],  // 柔和红
  ['#73c0de', '#13c2c2'],  // 青绿色
  ['#91d5ff', '#69c0ff'],  // 极浅天蓝
  ['#b37feb', '#9254de'],  // 薰荷紫
  ['#d3adf7', '#b37feb'],  // 浅薰衣草紫
  ['#ffadd2', '#f759ab'],  // 浅粉色
  ['#ffccc7', '#ff7875'],  // 浅珊瑚色
  ['#ff9c6e', '#ff7a45'],   // 柔和珊瑚橙

  // 扩展色彩系列 - 特殊色调 (20种)
  ['#2f54eb', '#0958d9'],  // 靛蓝
  ['#722ed1', '#391085'],  // 紫色
  ['#52c41a', '#237804'],  // 绿色
  ['#fa8c16', '#d46b08'],  // 橙色
  ['#f5222d', '#cf1322'],  // 红色
  ['#eb2f96', '#9e1068'],  // 粉色系
  ['#13c2c2', '#08979c'],  // 青色系
  ['#fa8c16', '#d46b08'],  // 橙色系
  ['#f5222d', '#cf1322'],  // 红色系
  ['#1890ff', '#0050b3'],  // 蓝色系
  ['#52c41a', '#237804'],  // 绿色系
  ['#ffd666', '#ffc53d'],  // 浅金色系
  ['#f5222d', '#cf1322'],  // 红色系
  ['#722ed1', '#391085'],  // 紫色系
  ['#fa8c16', '#d46b08'],  // 橙色系
  ['#eb2f96', '#9e1068'],  // 粉色系
  ['#13c2c2', '#08979c'],  // 青色系
  ['#595959', '#262626'],  // 灰色系
  ['#a0d911', '#7cb305'],  // 黄绿系
  ['#8c8cff', '#5cadff'],  // 浅蓝色
  ['#b4d3ff', '#722ed1'],  // 粉紫色
  ['#d9d7ff', '#52c41a'],  // 草绿色
  ['#ffd591', '#fa8c16'],  // 柔和橙
  ['#ff7875', '#ff4d4f'],  // 柔和红
  ['#73c0de', '#13c2c2'],  // 青绿色
  ['#91d5ff', '#69c0ff'],  // 极浅天蓝
  ['#b37feb', '#9254de'],  // 薰荷紫
  ['#d3adf7', '#b37feb'],  // 浅薰衣草紫
  ['#ffadd2', '#f759ab'],  // 浅粉色
  ['#ffccc7', '#ff7875'],  // 浅珊瑚色
  ['#ff9c6e', '#ff7a45'],  // 柔和珊瑚橙
  ['#8c8cff', '#5cadff'],  // 浅蓝色
  ['#b4d3ff', '#722ed1'],  // 粉紫色
  ['#d9d7ff', '#52c41a'],  // 草绿色
  ['#ffd591', '#fa8c16'],  // 柔和橙
  ['#ff7875', '#ff4d4f'],  // 柔和红
  ['#73c0de', '#13c2c2'],  // 青绿色
  ['#91d5ff', '#69c0ff'],  // 极浅天蓝
  ['#b37feb', '#9254de'],  // 薰荷紫
  ['#d3adf7', '#b37feb'],  // 浅薰衣草紫
  ['#ffadd2', '#f759ab'],  // 浅粉色
  ['#ffccc7', '#ff7875'],  // 浅珊瑚色
  ['#ff9c6e', '#ff7a45']   // 柔和珊瑚橙
];

export const TwoYearSpuSpecSales: React.FC<TwoYearSpuSpecSalesProps> = ({ data, loading }) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);

  // 处理数据
  const processData = (rawData: DataItem[]) => {
    console.log('原始数据:', rawData);

    if (!rawData || rawData.length === 0) return {
      months: [],
      spus: [],
      series: [],
      spuColorMap: {},
      chartTitle: null
    };

    // 获取所有唯一的SPU名称（优先使用tooltip_title）
    const spus = Array.from(new Set(rawData.map(item =>
      item.tooltip_title || item.spu_name || '未知SPU'
    )));
    console.log('SPU列表:', spus);

    const spuColorMap: { [key: string]: string } = {};
    spus.forEach((spu, index) => {
      spuColorMap[spu] = colors[index % colors.length][0];
    });

    // 获取所有月份并排序 - 处理 "2024-04" 格式
    const allMonths = new Set<string>();
    rawData.forEach((item, index) => {
      console.log(`数据项 ${index}:`, item);

      let monthValue = item.月份;

      if (monthValue) {
        // 处理 "2024-04" 格式，提取月份部分
        let monthDisplay = '';
        if (monthValue.includes('-')) {
          // 格式如 "2024-04"
          const [year, month] = monthValue.split('-');
          monthDisplay = `${parseInt(year)}年${parseInt(month)}月`;
          console.log(`找到月份字段: ${monthValue} -> ${monthDisplay}`);
        } else {
          // 格式如 "4" 或 "04"
          const month = parseInt(monthValue);
          monthDisplay = `${month}月`;
          console.log(`找到月份字段: ${monthValue} -> ${monthDisplay}`);
        }
        allMonths.add(monthDisplay);
      } else {
        console.log(`数据项 ${index} 没有找到月份字段，可用字段:`, Object.keys(item));
      }
    });

    // 按年月排序
    const months = Array.from(allMonths).sort((a, b) => {
      // 解析年月进行排序
      const parseYearMonth = (str: string) => {
        if (str.includes('年')) {
          const [year, month] = str.replace('年', '-').replace('月', '').split('-').map(Number);
          return year * 100 + month;
        }
        return parseInt(str);
      }
      return parseYearMonth(a) - parseYearMonth(b);
    });
    console.log('最终月份列表:', months);

    // 为每个SPU创建数据系列
    const allSeries = spus.map(spu => {
      const data = months.map(month => {
        // 查找对应的数据 - 需要匹配 "2024-04" 格式
        const matchingItem = rawData.find(item => {
          const itemSpu = item.tooltip_title || item.spu_name || '未知SPU';
          const itemMonthValue = item.月份;

          // 将显示格式 "2024年4月" 转换回数据格式 "2024-04" 进行匹配
          let matchMonth = '';
          if (month.includes('年') && month.includes('月')) {
            const [year, monthNum] = month.replace('年', '-').replace('月', '').split('-').map(Number);
            matchMonth = `${parseInt(year)}-${parseInt(monthNum).toString().padStart(2, '0')}`;
          }

          return itemSpu === spu && itemMonthValue === matchMonth;
        });

        return matchingItem ? matchingItem.销售金额 || 0 : 0;
      });

      console.log(`SPU ${spu} 的数据:`, data);

      return {
        name: spu,
        type: 'line',
        stack: 'Total',
        smooth: true,
        lineStyle: {
          width: 0
        },
        showSymbol: false,
        areaStyle: {
          opacity: 0.8
        },
        emphasis: {
          focus: 'series'
        },
        data: data
      };
    });

    // 过滤出有实际销售数据的SPU（排除所有数据都为0的SPU）
    const series = allSeries.filter(item => {
      const hasData = item.data.some(value => value > 0);
      if (!hasData) {
        console.log(`SPU ${item.name} 没有销售数据，已过滤`);
      }
      return hasData;
    });

    // 获取有数据的SPU列表，用于图例显示
    const validSpus = series.map(item => item.name);

    // 重新构建颜色映射，只包含有数据的SPU
    const finalSpuColorMap: { [key: string]: string } = {};
    validSpus.forEach((spu, index) => {
      finalSpuColorMap[spu] = colors[index % colors.length][0];
    });

    console.log('有效SPU列表:', validSpus);

    const result = {
      months,
      spus: validSpus,
      series,
      spuColorMap: finalSpuColorMap,
      chartTitle: rawData[0]?.title || null
    };

    console.log('处理后的数据:', result);
    return result;
  };

  // 初始化图表
  const initChart = () => {
    if (!chartRef.current) return;

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    const { months, spus, series, spuColorMap, chartTitle } = processData(data);

    const option: EChartsOption = {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          label: {
            backgroundColor: '#6a7985'
          }
        },
        formatter: (params: any[]) => {
          if (!params || params.length === 0) return '';

          // 显示月份
          const month = params[0].axisValue;
          let result = `
            <div class="font-bold mb-2">${month}</div>
          `;

          // 显示图表标题（如果有）
          if (chartTitle) {
            result = `<div class="font-bold mb-2" style="color:#1890ff;">${chartTitle}</div>` + result;
          }

          // 按销量从大到小排序，只显示有销量的SPU
          const validParams = params.filter(param => param.value > 0).sort((a, b) => b.value - a.value);

          if (validParams.length === 0) {
            result += '<div style="color:#999;">暂无数据</div>';
            return result;
          }

          validParams.forEach(param => {
            // 使用SPU到颜色的映射，确保颜色一致
            const color = spuColorMap[param.seriesName] || param.color;
            const marker = `<span style="display:inline-block;margin-right:4px;border-radius:5px;width:12px;height:8px;background-color:${color};"></span>`;
            result += `<div style="margin:2px 0;">${marker}${param.seriesName}：${param.value.toLocaleString()} </div>`;
          });

          return result;
        }
      },
      legend: {
        data: series.map((item, index) => ({
          name: item.name,
          itemStyle: {
            color: colors[index % colors.length][0]
          }
        })),
        top: 30,
        type: 'scroll',
        textStyle: {
          fontSize: 12
        },
        icon: 'circle',
        itemWidth: 12,
        itemHeight: 12,
        itemGap: 15
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '10%',
        containLabel: true,
        top: 80
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: months,
        axisLabel: {
          interval: 0,
          rotate: 45,
          formatter: (value: string) => {
            return value; // 直接显示 "2024年4月" 格式
          }
        }
      },
      yAxis: {
        type: 'value',
        name: '销售金额',
        axisLabel: {
          formatter: (value: number) => value.toLocaleString()
        }
      },
      series: series.map((item, index) => ({
        ...item,
        areaStyle: {
          opacity: 0.8,
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            {
              offset: 0,
              color: colors[index % colors.length][0]
            },
            {
              offset: 1,
              color: colors[index % colors.length][1]
            }
          ])
        }
      }))
    };

    chartInstance.current.setOption(option);
  };

  // 处理窗口大小变化
  useEffect(() => {
    const handleResize = debounce(() => {
      if (chartInstance.current) {
        chartInstance.current.resize();
      }
    }, 300);

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      handleResize.cancel();
    };
  }, []);

  // 更新图表数据
  useEffect(() => {
    if (data && data.length > 0) {
      initChart();
    }
  }, [data]);

  // 销毁图表实例
  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="w-full h-[500px] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div ref={chartRef} className="w-full h-[500px]" />
  );
};

export default TwoYearSpuSpecSales;